/**
 * @file MapEngine — framework-agnostic Google Maps adapter for the store locator.
 *
 * Owns the map, AdvancedMarkerElement markers, numbered clustering, the radius
 * circle, and a single reused InfoWindow. Exposes an options/callbacks/methods
 * contract (see types.ts) and never leaks `google.*` to callers — the seam a
 * future React hook wraps.
 *
 * Invariants:
 * - The visible set is always `withinRadius ∩ activeCategories`; the list is fed
 *   only from `onStoresChanged`, never from the full dataset.
 * - Radius anchor modes (opts.panFollowsRadius):
 *   • follow (default): panning re-centers the radius + gating to the map center
 *     (idle-driven), highlighting dealers in the panned-to area.
 *   • locked: the anchor is FIXED, set only by setCenter (address / geolocation /
 *     Nashville fallback / radius change); panning leaves the circle + gated set
 *     put — to see another area the user enters a new address.
 * - Zoom is free (so clicking a cluster can zoom in to expand it); the radius
 *   dropdown is a MARKER FILTER (only stores within X miles of the anchor show).
 *   `minZoom` caps zoom-out.
 */
import type { MarkerClusterer } from "@googlemaps/markerclusterer";
import type {
  Category,
  LatLng,
  MapEngineCallbacks,
  MapEngineInstance,
  MapEngineOptions,
  Store,
} from "./types";
import { loadGoogleMaps } from "./loadSdk";
import { buildMarkerContent, setMarkerActive } from "./markers";
import { createClusterer } from "./clusterer";
import { boundsFromRadius, haversineMiles, milesToMeters } from "./geo";

type MarkerRec = {
  store: Store;
  marker: google.maps.marker.AdvancedMarkerElement;
  content: HTMLElement;
};

/**
 * Framing zoom for a radius — used as the initial zoom and when the radius
 * changes (via fitBounds). Zoom stays free afterward (users can zoom/expand
 * clusters); this only sets a sensible starting frame per radius.
 */
function zoomForRadius(radiusMiles: number): number {
  if (radiusMiles <= 15) return 11;
  if (radiusMiles <= 30) return 10;
  if (radiusMiles <= 50) return 9;
  if (radiusMiles <= 100) return 8;
  if (radiusMiles <= 300) return 7;
  return 5;
}

export function MapEngine(opts: MapEngineOptions, cb: MapEngineCallbacks = {}) {
  let map: google.maps.Map;
  let clusterer: MarkerClusterer;
  let infoWindow: google.maps.InfoWindow;
  let circle: google.maps.Circle | null = null;
  let idleListener: google.maps.MapsEventListener | null = null;

  let stores: Store[] = [];
  const markerRecs = new Map<string, MarkerRec>();
  const activeFilters: Record<Category, boolean> = {
    company: true,
    premium: true,
    dealer: true,
  };

  let center: LatLng = opts.center;
  let radiusMiles = opts.radiusMiles;
  let activeUuid: string | null = null;
  let instance: MapEngineInstance;

  const renderMarker = opts.renderMarkerContent || buildMarkerContent;
  const follow = opts.panFollowsRadius !== false; // default: follow-on-pan

  function toLatLng(g: google.maps.LatLng | null | undefined): LatLng {
    return g ? { lat: g.lat(), lng: g.lng() } : center;
  }

  function clearActive() {
    if (activeUuid) {
      const prev = markerRecs.get(activeUuid);
      if (prev) setMarkerActive(prev.content, false);
    }
    activeUuid = null;
  }

  /** Rebuild AdvancedMarkerElement objects for the full store set (not yet placed). */
  function buildMarkers() {
    markerRecs.forEach((rec) => (rec.marker.map = null));
    markerRecs.clear();
    clearActive();

    stores.forEach((store) => {
      const content = renderMarker(store);
      const marker = new google.maps.marker.AdvancedMarkerElement({
        position: { lat: store.lat, lng: store.lng },
        content,
        gmpClickable: true,
        // Company/studio pins sit above clusters (they're never clustered).
        zIndex: store.category === "company" ? 100000 : undefined,
      });
      marker.addListener("gmp-click", () => {
        cb.onMarkerClick?.(store);
        openInfoWindow(store.uuid);
      });
      markerRecs.set(store.uuid, { store, marker, content });
    });
  }

  /** Gate by radius ∩ active category, recompute distances, refresh clusters + list. */
  function refresh() {
    const visible: Store[] = [];
    const clustered: google.maps.marker.AdvancedMarkerElement[] = [];

    markerRecs.forEach((rec) => {
      const d = haversineMiles(center, { lat: rec.store.lat, lng: rec.store.lng });
      rec.store.distance = d;
      const show = d <= radiusMiles && activeFilters[rec.store.category];

      if (rec.store.category === "company") {
        // Company/studio pins are never clustered — placed directly, always visible.
        rec.marker.map = show ? map : null;
        if (show) visible.push(rec.store);
      } else if (show) {
        visible.push(rec.store);
        clustered.push(rec.marker);
      }
    });

    clusterer.clearMarkers();
    clusterer.addMarkers(clustered);

    visible.sort((a, b) => (a.distance || 0) - (b.distance || 0));
    cb.onStoresChanged?.(visible, center, radiusMiles);
  }

  function updateCircle() {
    if (!circle) return;
    circle.setCenter(center);
    circle.setRadius(milesToMeters(radiusMiles));
  }

  function defaultInfoContent(store: Store): string {
    return (
      `<div class="locatorTip"><strong>${store.name}</strong>` +
      `<br>${store.addr_1 || ""}<br>${store.addr_2 || ""}</div>`
    );
  }

  function openInfoWindow(uuid: string) {
    const rec = markerRecs.get(uuid);
    if (!rec) return;
    clearActive();
    activeUuid = uuid;
    setMarkerActive(rec.content, true);
    const content = opts.renderInfoWindowContent
      ? opts.renderInfoWindowContent(rec.store)
      : defaultInfoContent(rec.store);
    infoWindow.setContent(content);
    infoWindow.open({ anchor: rec.marker, map });
  }

  // Follow mode only: panning re-anchors the radius/gating to the map center.
  function handleIdle() {
    center = toLatLng(map.getCenter());
    updateCircle();
    refresh();
    cb.onBoundsSettled?.(center, radiusMiles, boundsFromRadius(center, radiusMiles));
  }

  // ---- public instance methods ----
  function setStores(next: Store[]) {
    stores = next;
    buildMarkers();
    refresh();
  }

  /**
   * Recenter and frame the current radius (fitBounds to the radius circle). Zoom
   * stays free afterward — the radius is a MARKER FILTER, not a zoom lock, so
   * users can still zoom (and expand clusters). `minZoom` caps zoom-out.
   */
  function setCenter(next: LatLng, nextRadius?: number) {
    center = next;
    if (typeof nextRadius === "number") radiusMiles = nextRadius;
    updateCircle();
    const b = circle?.getBounds();
    if (b) map.fitBounds(b);
    else map.setCenter(center);
    refresh();
    // In LOCKED mode this is the only area-change/refetch trigger. In FOLLOW mode
    // the idle handler fires it (on pan), so skip here to avoid double-firing.
    if (!follow) {
      cb.onBoundsSettled?.(center, radiusMiles, boundsFromRadius(center, radiusMiles));
    }
  }

  function setCategoryFilter(category: Category, on: boolean) {
    activeFilters[category] = on;
    refresh();
    cb.onFilterChange?.({ ...activeFilters });
  }

  function setCategoryFilters(next: Record<Category, boolean>) {
    activeFilters.company = next.company;
    activeFilters.premium = next.premium;
    activeFilters.dealer = next.dealer;
    refresh();
    cb.onFilterChange?.({ ...activeFilters });
  }

  function getState() {
    return { center, zoom: map.getZoom() ?? zoomForRadius(radiusMiles), radiusMiles };
  }

  function destroy() {
    if (idleListener) idleListener.remove();
    clusterer.clearMarkers();
    markerRecs.forEach((rec) => (rec.marker.map = null));
    markerRecs.clear();
    if (circle) circle.setMap(null);
    infoWindow.close();
  }

  async function init(): Promise<MapEngineInstance> {
    await loadGoogleMaps();

    map = new google.maps.Map(opts.container, {
      mapId: opts.mapId,
      center: opts.center,
      // Zoom is free (needed so clicking a cluster can zoom in to expand it).
      // `minZoom` caps zoom-out; the radius dropdown filters which markers show.
      zoom: zoomForRadius(radiusMiles),
      minZoom: opts.minZoom,
      disableDefaultUI: true,
      zoomControl: false, // hide the +/- buttons; wheel/pinch/cluster-click still zoom
      clickableIcons: false,
      gestureHandling: "greedy",
      // Pass the string value directly (matches the ColorScheme enum values);
      // avoids a hard runtime dependency on google.maps.ColorScheme existing.
      colorScheme: (opts.colorScheme ??
        "FOLLOW_SYSTEM") as unknown as google.maps.MapOptions["colorScheme"],
      restriction: opts.restriction
        ? { latLngBounds: opts.restriction, strictBounds: false }
        : undefined,
    });

    infoWindow = new google.maps.InfoWindow({ maxWidth: 300 });
    infoWindow.addListener("closeclick", clearActive);

    if (opts.showRadiusCircle !== false) {
      circle = new google.maps.Circle({
        map,
        center: opts.center,
        radius: milesToMeters(radiusMiles),
        fillOpacity: 0.05,
        strokeOpacity: 0.25,
        strokeWeight: 1,
        clickable: false,
      });
    }

    clusterer = createClusterer(map);
    if (follow) idleListener = map.addListener("idle", handleIdle);

    instance = {
      setStores,
      setCenter,
      setCategoryFilter,
      setCategoryFilters,
      openInfoWindow,
      getState,
      destroy,
    };
    cb.onReady?.(instance);
    return instance;
  }

  return { init };
}
